/* ALON · package size without reloading (vanilla by weight)
 *
 * The size rows (snippets/alon-weight-selector) are real links between the
 * separate weight products (250 g, 1 kg …): without this file every choice
 * is a normal page load. This file turns a plain click on a row into a swap
 * of the main product section, so the page stays where it is:
 *
 * 1. Target: the other product's variant with the length that is CHECKED in
 *    this product's length picker right now (row data-variants
 *    "length=id;", the pairs assets/alon-shop.js followVariant also uses).
 *    No such length in stock → the product's first available variant, and
 *    the change is announced.
 * 2. Only that product's main section is fetched
 *    (?variant=…&section_id=<this product-info's data-section>), checked
 *    (product-info with the expected data-product-id) and swapped in with
 *    Dawn's own HTMLUpdateUtility.viewTransition and the product-info's
 *    pre/post callbacks (payment button re-init). Responses are kept for a
 *    minute and fetched ahead on hover / focus / touch.
 * 3. Glue: the old product's in-flight length request is aborted and it may
 *    no longer touch the address; its Add to cart is disabled while the new
 *    product loads; an open photo lightbox is closed with its own hide()
 *    (scroll lock and focus trap released, focus then goes to the new
 *    current row) before the moved modal is removed; document.title is
 *    set before history.pushState (GA4 reads it then); canonical, og:url
 *    and og:title follow; the new product form gets the ALON cart adapter
 *    (window.__alonCartNotification); focus goes to the new current row and
 *    one polite live region (outside product-info) announces the choice;
 *    "alon:product-swapped" is dispatched on document (mobile sticky bar,
 *    buy-now label).
 * 4. Back / forward: popstate compares the address (never history.state,
 *    which Dawn's replaceState wipes) with the product shown and swaps
 *    without pushing. Such a switch stopped before it finished (Forward
 *    again, or a click on the current row) gives the title, and with
 *    replaceState the address, back to the product still shown.
 * Any failure (network, 429/500, unexpected response) re-enables the button
 * and loads the page normally — once.
 *
 * Loaded by sections/main-product.liquid only on weight packs, only while
 * Theme settings → ALON · Packs → "Switch package size without reloading" is
 * on, and never in the theme editor (also checked here). Runs once per page.
 * Dawn's product-info.js, product-form.js and global.js are not changed.
 * No tracking code.
 */
(function () {
  'use strict';

  if (window.__alonWeightSwitch) return;
  window.__alonWeightSwitch = true;
  if (window.Shopify && window.Shopify.designMode) return;

  var ROW = 'a[data-alon-weight-link]';
  var TTL = 60000;
  var MAX_CACHED = 6;

  var cache = new Map();
  var active = null; // the switch in progress
  var shown = null; // the product-info this file last swapped in
  var leaving = false; // a normal page load has been started
  var live = null;

  function supported() {
    return (
      typeof HTMLUpdateUtility !== 'undefined' &&
      typeof HTMLUpdateUtility.viewTransition === 'function' &&
      !!(window.history && window.history.pushState) &&
      typeof window.fetch === 'function' &&
      typeof window.DOMParser === 'function'
    );
  }

  function enabled(root) {
    return (
      !leaving &&
      !!root &&
      root.hasAttribute('data-alon-weight-switch') &&
      !(window.Shopify && window.Shopify.designMode) &&
      supported()
    );
  }

  /* The main product's product-info only (not quick-add or other modals). */
  function productInfoOf(el) {
    var pi = el && el.closest ? el.closest('product-info') : null;
    if (!pi || !pi.id || pi.id.indexOf('MainProduct-') !== 0) return null;
    if (pi.closest('quick-add-modal, modal-dialog')) return null;
    return pi;
  }

  function safeDecode(value) {
    try {
      return decodeURIComponent(value);
    } catch (e) {
      return value;
    }
  }

  function pathOf(url) {
    var path = String(url || '').split('#')[0].split('?')[0];
    if (/^https?:\/\//i.test(path)) path = path.replace(/^https?:\/\/[^/]+/i, '');
    return safeDecode(path).replace(/\/+$/, '');
  }

  function parsePairs(value) {
    var map = {};
    (value || '').split(';').forEach(function (pair) {
      var at = pair.lastIndexOf('=');
      if (at > 0) map[pair.slice(0, at)] = pair.slice(at + 1);
    });
    return map;
  }

  /* The length chosen right now: the checked input of the length picker. */
  function checkedLength(pi) {
    var selects = pi.querySelector('variant-selects');
    if (selects) {
      var radio = selects.querySelector('input[type="radio"]:checked');
      if (radio) return radio.value;
      var select = selects.querySelector('select');
      if (select) return select.value;
    }
    var fixed = pi.querySelector('.ap-static-option__value');
    return fixed ? fixed.textContent.trim() : '';
  }

  function target(pi, link) {
    var length = checkedLength(pi);
    return {
      base: link.getAttribute('data-url') || '',
      variant: (length && parsePairs(link.getAttribute('data-variants'))[length]) || '',
      productId: link.getAttribute('data-product-id') || '',
      title: link.getAttribute('data-alon-doc-title') || '',
      length: length
    };
  }

  function sectionUrl(pi, t) {
    return (
      t.base +
      '?' +
      (t.variant ? 'variant=' + encodeURIComponent(t.variant) + '&' : '') +
      'section_id=' +
      encodeURIComponent(pi.getAttribute('data-section') || '')
    );
  }

  function pageUrl(t, variantId) {
    var id = variantId || t.variant;
    return t.base + (id ? '?variant=' + id : '');
  }

  /* ---------- Section fetch with a small cache ---------- */
  function load(url, signal) {
    var now = Date.now();
    var hit = cache.get(url);
    if (hit && now - hit.time < TTL && !(hit.signal && hit.signal.aborted)) return hit.promise;
    var promise = window
      .fetch(url, { credentials: 'same-origin', signal: signal })
      .then(function (response) {
        if (!response.ok) {
          var error = new Error('HTTP ' + response.status);
          error.status = response.status;
          throw error;
        }
        return response.text();
      });
    var entry = { time: now, promise: promise, signal: signal || null };
    cache.delete(url);
    cache.set(url, entry);
    promise.catch(function () {
      if (cache.get(url) === entry) cache.delete(url);
    });
    while (cache.size > MAX_CACHED) cache.delete(cache.keys().next().value);
    return promise;
  }

  function prime(event) {
    var link = event.target && event.target.closest ? event.target.closest(ROW) : null;
    if (!link || link.getAttribute('aria-current') === 'page') return;
    if (event.type === 'pointerenter' && event.pointerType === 'touch') return;
    var pi = productInfoOf(link);
    if (!pi || !enabled(link.closest('[data-alon-weight]'))) return;
    if (navigator.connection && navigator.connection.saveData) return;
    load(sectionUrl(pi, target(pi, link))).catch(function () {});
  }

  /* ---------- Live region (outside product-info, so it survives swaps) ---------- */
  function liveRegion() {
    if (live && live.isConnected) return live;
    live = document.getElementById('AlonWeightLive');
    if (!live) {
      live = document.createElement('div');
      live.id = 'AlonWeightLive';
      live.className = 'visually-hidden';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('aria-atomic', 'true');
      document.body.appendChild(live);
    }
    return live;
  }

  function say(message) {
    var region = liveRegion();
    region.textContent = '';
    if (!message) return;
    window.setTimeout(function () {
      region.textContent = message;
    }, 60);
  }

  function fill(template, values) {
    return String(template || '').replace(/%(\w+)%/g, function (all, key) {
      return values[key] != null ? values[key] : '';
    });
  }

  function announce(pi, requested) {
    var root = pi.querySelector('[data-alon-weight]');
    if (!root) return;
    var row = root.querySelector(ROW + '[aria-current="page"]');
    var size = row ? row.getAttribute('data-alon-size') || '' : '';
    var priceEl = row ? row.querySelector('.aw__var:not([hidden]) .aw__price') : null;
    var price = priceEl ? priceEl.textContent.trim() : '';
    var length = checkedLength(pi);
    var values = { size: size, price: price, length: length, requested: requested || '' };
    var message = fill(root.getAttribute(length ? 'data-aw-announce' : 'data-aw-announce-short'), values);
    if (requested && length && requested !== length) {
      message = fill(root.getAttribute('data-aw-fallback'), values) + ' ' + message;
    }
    say(message.trim());
  }

  /* ---------- Old product: hold it still while the new one loads ---------- */
  function hold(job, on) {
    var pi = job.pi;
    var form = pi.querySelector('product-form');
    var button = form ? form.querySelector('[type="submit"]') : null;
    if (on) {
      job.wasDisabled = button ? button.hasAttribute('disabled') : true;
      if (button) button.setAttribute('disabled', 'disabled');
      pi.classList.add('is-alon-switching');
      if (job.root) job.root.setAttribute('aria-busy', 'true');
      if (job.link) job.link.classList.add('is-loading');
    } else {
      if (button && !job.wasDisabled) button.removeAttribute('disabled');
      pi.classList.remove('is-alon-switching');
      if (job.root) job.root.removeAttribute('aria-busy');
      if (job.link) job.link.classList.remove('is-loading');
    }
  }

  /* restore: the switch is stopped for good (not replaced by another one).
     A back/forward switch already changed the title (and the address is the
     other product's), so both go back to the product still shown. */
  function cancel(restore) {
    var job = active;
    if (!job) return;
    active = null;
    if (job.controller) {
      try {
        job.controller.abort();
      } catch (e) {}
    }
    hold(job, false);
    if (restore && !job.push) restoreShown(job.pi);
  }

  function restoreShown(pi) {
    if (!pi || !pi.isConnected) return;
    var title = pi.getAttribute('data-alon-doc-title');
    if (title) document.title = title;
    var base = pi.getAttribute('data-url') || '';
    if (!base || pathOf(window.location.pathname) === pathOf(base)) return;
    var idInput = pi.querySelector('product-form input[name="id"]');
    var id = idInput && idInput.value ? idInput.value : '';
    try {
      window.history.replaceState(window.history.state, '', base + (id ? '?variant=' + id : ''));
    } catch (e) {}
  }

  /* The product photo lightbox (Dawn's product-modal, moved to <body>):
     closed the way its own close button does (scroll lock off, focus back
     to the photo that opened it). Returns whether it was open. */
  function closeLightbox(pi) {
    var modal = document.getElementById('ProductModal-' + (pi.getAttribute('data-section') || ''));
    if (!modal || !modal.hasAttribute('open')) return false;
    try {
      if (typeof modal.hide === 'function') modal.hide();
      else modal.removeAttribute('open');
    } catch (e) {
      modal.removeAttribute('open');
    }
    if (modal.hasAttribute('open')) modal.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');
    return true;
  }

  /* Normal page load instead — exactly once. */
  function leave(url, reload) {
    if (leaving) return;
    leaving = true;
    if (reload) {
      window.location.reload();
    } else {
      window.location.assign(url);
    }
  }

  function fail(job) {
    if (active === job) active = null;
    hold(job, false);
    if (job.push) {
      leave(pageUrl(job.t));
    } else {
      leave(null, true);
    }
  }

  /* ---------- The switch ---------- */
  function start(pi, t, options) {
    if (leaving) return;
    if (active) cancel();
    var root = pi.querySelector('[data-alon-weight]');
    var job = {
      pi: pi,
      root: root,
      t: t,
      key: sectionUrl(pi, t),
      push: !!options.push,
      retried: !!options.retried,
      closedLightbox: !!options.closedLightbox,
      link: options.link || null,
      controller: typeof window.AbortController === 'function' ? new window.AbortController() : null
    };
    active = job;
    liveRegion();
    hold(job, true);
    load(job.key, job.controller ? job.controller.signal : undefined).then(
      function (text) {
        if (active !== job) return;
        try {
          finish(job, text);
        } catch (error) {
          fail(job);
        }
      },
      function (error) {
        if (active !== job) return;
        if (error && error.name === 'AbortError') {
          active = null;
          hold(job, false);
          return;
        }
        fail(job);
      }
    );
  }

  function finish(job, text) {
    var pi = job.pi;
    var t = job.t;
    var doc = new DOMParser().parseFromString(text, 'text/html');
    var fresh = doc.querySelector('product-info[data-product-id]');
    if (
      !fresh ||
      (t.productId && fresh.getAttribute('data-product-id') !== String(t.productId)) ||
      !fresh.querySelector('product-form')
    ) {
      fail(job);
      return;
    }

    /* A length picked on the old product while this loaded wins (once). */
    if (job.push && !job.retried && job.link) {
      var now = target(pi, job.link);
      if (now.length && now.length !== t.length && sectionUrl(pi, now) !== job.key) {
        active = null;
        hold(job, false);
        start(pi, now, { push: true, link: job.link, retried: true });
        return;
      }
    }

    var section = pi.getAttribute('data-section') || '';
    var idInput = fresh.querySelector('product-form input[name="id"]');
    var variantId = idInput && idInput.value ? idInput.value : t.variant;
    var url = pageUrl(t, variantId);
    var title = fresh.getAttribute('data-alon-doc-title') || t.title;
    /* The lightbox opened while this loaded: close it properly first (it
       hands focus back to its photo, inside the old product). */
    var lightboxWasOpen = closeLightbox(pi) || !!job.closedLightbox;
    var focusInside = pi.contains(document.activeElement);
    if (
      lightboxWasOpen &&
      (!document.activeElement || document.activeElement === document.body || document.activeElement === document.documentElement)
    ) {
      focusInside = true;
    }

    /* The old product may no longer write the address: abort its length
       request, and make a callback that already got its response skip
       history (product-info.js updateURL honours data-update-url). */
    if (pi.abortController) {
      try {
        pi.abortController.abort();
      } catch (e) {}
    }
    pi.setAttribute('data-update-url', 'false');

    /* The lightbox modal moved itself to <body> (global.js ModalDialog):
       remove it first, as product-info.js does for its own product swap
       (closed above, so no scroll lock or focus trap is left behind). */
    var modal = document.getElementById('ProductModal-' + section);
    if (modal) modal.remove();

    active = null;
    var swapped = null;
    try {
      HTMLUpdateUtility.viewTransition(pi, fresh, pi.preProcessHtmlCallbacks, pi.postProcessHtmlCallbacks);
      swapped = pi.previousElementSibling;
    } catch (error) {
      swapped = null;
    }
    if (
      !swapped ||
      swapped.tagName !== 'PRODUCT-INFO' ||
      swapped.getAttribute('data-product-id') !== fresh.getAttribute('data-product-id')
    ) {
      hold(job, false);
      leave(url);
      return;
    }
    /* From here on only the new node: the old one keeps its id for 500 ms. */
    shown = swapped;

    if (title) document.title = title;
    if (job.push) window.history.pushState(null, '', url);
    updateHead(swapped);
    wireCart(swapped);

    var row = swapped.querySelector('[data-alon-weight] ' + ROW + '[aria-current="page"]');
    if (row && (job.push || focusInside)) {
      try {
        row.focus({ preventScroll: true });
      } catch (e) {
        row.focus();
      }
    }
    announce(swapped, job.push ? t.length : '');

    document.dispatchEvent(
      new CustomEvent('alon:product-swapped', {
        detail: {
          productId: swapped.getAttribute('data-product-id'),
          variantId: variantId,
          url: url,
          productInfo: swapped
        }
      })
    );
  }

  function setMeta(selector, attribute, value) {
    var el = document.querySelector(selector);
    if (el && value) el.setAttribute(attribute, value);
  }

  function updateHead(pi) {
    var canonical = pi.getAttribute('data-alon-canonical');
    setMeta('link[rel="canonical"]', 'href', canonical);
    setMeta('meta[property="og:url"]', 'content', canonical);
    var ogTitle = pi.getAttribute('data-alon-og-title');
    setMeta('meta[property="og:title"]', 'content', ogTitle);
    setMeta('meta[name="twitter:title"]', 'content', ogTitle);
  }

  /* A product form created after page load has no cart (product-form.js
     looks for <cart-notification>/<cart-drawer> only), so Add to cart would
     redirect to /cart: give it the ALON cart adapter
     (sections/alon-header.liquid), as the header does on DOMContentLoaded. */
  function wireCart(pi) {
    var adapter = window.__alonCartNotification;
    if (!adapter) return;
    var forms = pi.querySelectorAll('product-form');
    for (var i = 0; i < forms.length; i++) {
      if (!forms[i].cart) forms[i].cart = adapter;
    }
  }

  function currentProductInfo() {
    if (shown && shown.isConnected && shown.style.display !== 'none') return shown;
    var list = document.querySelectorAll('product-info[id^="MainProduct-"]');
    for (var i = 0; i < list.length; i++) {
      if (list[i].style.display !== 'none' && list[i].querySelector('[data-alon-weight]')) return list[i];
    }
    return null;
  }

  /* ---------- Events (document level, bound once) ---------- */
  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var link = event.target && event.target.closest ? event.target.closest(ROW) : null;
    if (!link) return;
    var pi = productInfoOf(link);
    if (!pi || !enabled(link.closest('[data-alon-weight]'))) return;
    if (link.target && link.target !== '_self') return;
    event.preventDefault();
    if (link.getAttribute('aria-current') === 'page') {
      /* Already this product: stop a switch that is still loading (a
         back/forward one also hands the title and address back to it). */
      if (active && active.pi === pi) cancel(true);
      return;
    }
    var t = target(pi, link);
    if (active && active.pi === pi && active.key === sectionUrl(pi, t)) return;
    start(pi, t, { push: true, link: link });
  });

  /* Nothing from the old product is added while the new one loads. */
  document.addEventListener(
    'submit',
    function (event) {
      if (active && active.pi.contains(event.target)) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    true
  );

  document.addEventListener('pointerenter', prime, true);
  document.addEventListener('focusin', prime);
  document.addEventListener('touchstart', prime, { passive: true });

  window.addEventListener('popstate', function () {
    if (leaving) return;
    var pi = currentProductInfo();
    var root = pi ? pi.querySelector('[data-alon-weight]') : null;
    if (!pi || !enabled(root)) return;
    var path = pathOf(window.location.pathname);
    if (path === pathOf(pi.getAttribute('data-url'))) {
      /* Same product (a hash change, or back before a switch finished):
         the title a back/forward switch set goes back to this product. */
      if (active && active.pi === pi) cancel(true);
      return;
    }
    var links = root.querySelectorAll(ROW);
    var link = null;
    for (var i = 0; i < links.length; i++) {
      if (pathOf(links[i].getAttribute('data-url')) === path) link = links[i];
    }
    if (!link) {
      /* The address is another page this one cannot show: load it. */
      leave(null, true);
      return;
    }
    var t = target(pi, link);
    var match = /[?&]variant=(\d+)/.exec(window.location.search);
    t.variant = match ? match[1] : '';
    /* Back/forward closes an open photo lightbox right away, properly. */
    var closedLightbox = closeLightbox(pi);
    /* GA4 reads the title on popstate: set it now, before the fetch. */
    if (t.title) document.title = t.title;
    start(pi, t, { push: false, link: link, closedLightbox: closedLightbox });
  });

  /* Back from a fallback page load (bfcache): this page may switch again. */
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) leaving = false;
  });

  /* Cart quantities are part of the section (quantity rules): refetch after adds. */
  if (typeof window.subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    window.subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      cache.clear();
    });
  }
})();
